interface ExtendSelectionParams<T> {
  /** The selection before the click. */
  selected: readonly T[];
  /** The rows on screen, in the order shown. Ranges span only these. */
  visible: readonly T[];
  /** Key of the last row clicked, or `undefined` when nothing was. */
  anchor: string | undefined;
  /** The row shift-clicked. */
  target: T;
  /** Identifies a row; two rows with the same key are the same row. */
  keyOf: (row: T) => string;
}

/**
 * The selection after a shift+click on `target`, by the usual checklist
 * convention: `target` flips, and every visible row from `anchor` to `target`,
 * inclusive, takes the state it flipped to. Rows selected by the range are
 * appended to the selection in list order, each once; rows deselected by it
 * are dropped. Selected rows outside the range, visible or not, keep their
 * state.
 *
 * Without an anchor, or with one no longer visible (the list was searched
 * away from it), only `target` flips — a plain click.
 */
export function extendSelection<T>({
  selected,
  visible,
  anchor,
  target,
  keyOf,
}: ExtendSelectionParams<T>): T[] {
  const targetKey = keyOf(target);
  const selectedKeys = new Set(selected.map(keyOf));
  const targetIndex = visible.findIndex((row) => keyOf(row) === targetKey);
  const anchorIndex =
    anchor === undefined
      ? -1
      : visible.findIndex((row) => keyOf(row) === anchor);
  const range =
    anchorIndex < 0 || targetIndex < 0
      ? [target]
      : visible.slice(
          Math.min(anchorIndex, targetIndex),
          Math.max(anchorIndex, targetIndex) + 1,
        );

  if (selectedKeys.has(targetKey)) {
    const rangeKeys = new Set(range.map(keyOf));
    return selected.filter((row) => !rangeKeys.has(keyOf(row)));
  }
  return [...selected, ...range.filter((row) => !selectedKeys.has(keyOf(row)))];
}
